import * as stylex from '@stylexjs/stylex';
import _ from 'lodash';
import { Field, useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import { getFormFieldProps, getFormFieldWrapperProps } from '@/formbuilder/formbuilder.utilities';
import { TextField } from '@seeqdev/qomponents';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';

type MinMax = {
  min?: number;
  max?: number;
};

export type LowerUpperLimitsValue = {
  lowerBound: number;
  upperBound: number;
};

export interface LowerUpperLimitsIF extends ValidatingFormComponent<LowerUpperLimitsValue> {
  component: 'LowerUpperLimitsFormComponent';
  onLowerBoundChange: (lowerBound: number) => void;
  onUpperBoundChange: (upperBound: number) => void;
  lowerBoundLabel: string;
  upperBoundLabel: string;
  lowerBoundMinMax?: MinMax;
  upperBoundMinMax?: MinMax;
  inputClassNames?: string;
  inputXstyle?: stylex.StyleXStyles;
  label?: string;
  tooltip?: string;
}

export const LowerUpperLimitsFormComponent = (props: LowerUpperLimitsIF) => {
  const {
    testId = 'valueLimits',
    name,
    value = {} as LowerUpperLimitsValue,
    onLowerBoundChange,
    onUpperBoundChange,
    validation,
    upperBoundLabel,
    lowerBoundLabel,
    customErrorText,
    lowerBoundMinMax,
    upperBoundMinMax,
    inputClassNames,
  } = props;

  const { t } = useTranslation();
  const formState = useForm().getState();

  const defaultValidation = () => false;

  const appliedValidation = _.isFunction(validation) ? validation : defaultValidation;

  return (
    <FormFieldWrapper
      {...getFormFieldWrapperProps(props, ['id', 'tooltip'])}
      label={props.label}
      tooltip={props.tooltip}
      showError={appliedValidation(value as LowerUpperLimitsValue)}
      customErrorText={customErrorText}>
      <Field name={name} validate={appliedValidation}>
        {({ input, meta }) => {
          const { onChange, value } = getFormFieldProps(formState, input, meta, props);
          const cur = (value ?? {}) as Partial<LowerUpperLimitsValue>;
          const includeZero = (v?: number) => (_.isNil(v) ? '' : v);
          const getValue = (v: string) => (v === '' ? undefined : Number(v));
          const showError = appliedValidation(cur as LowerUpperLimitsValue);

          return (
            <div {...stylex.props(styles.limits)}>
              <div {...stylex.props(styles.lowerLimit)}>
                <p {...stylex.props(styles.label)}>{t(lowerBoundLabel)}</p>
                <TextField
                  testId={`${testId}Lower`}
                  autoComplete="off"
                  type="number"
                  size="sm"
                  min={lowerBoundMinMax?.min}
                  max={lowerBoundMinMax?.max}
                  extraClassNames={inputClassNames}
                  xstyle={props.inputXstyle}
                  value={includeZero(value.lowerBound)}
                  onChange={(e) => {
                    const lowerBound = getValue(e.target.value);
                    onLowerBoundChange(lowerBound as number);
                    onChange({ ...value, lowerBound });
                  }}
                  showError={showError}
                />
              </div>
              <div>
                <p {...stylex.props(styles.label)}>{t(upperBoundLabel)}</p>
                <TextField
                  testId={`${testId}Upper`}
                  autoComplete="off"
                  type="number"
                  size="sm"
                  min={upperBoundMinMax?.min}
                  max={upperBoundMinMax?.max}
                  extraClassNames={inputClassNames}
                  xstyle={props.inputXstyle}
                  value={includeZero(value.upperBound)}
                  onChange={(e) => {
                    const upperBound = getValue(e.target.value);
                    onUpperBoundChange(upperBound as number);
                    onChange({ ...value, upperBound });
                  }}
                  showError={showError}
                />
              </div>
            </div>
          );
        }}
      </Field>
    </FormFieldWrapper>
  );
};

const styles = stylex.create({
  limits: { display: 'flex', flexDirection: 'row', width: '100%' },
  lowerLimit: { marginRight: '0.625rem' },
  label: { margin: 0 },
});
